import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";
import useModalManager from "@/zustand/useModalManager";

interface DialogProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root> {
  /**
   * Optional unique identifier for modal management.
   * When provided, this dialog will participate in the single-popup constraint,
   * automatically closing when another managed modal opens.
   */
  modalId?: string;
  /** Whether the dialog is open */
  open?: boolean;
  /** Callback when the dialog's open state changes */
  onOpenChange?: (open: boolean) => void;
}

/**
 * Internal component for managed dialog with modal coordination.
 * This is separated to avoid conditional hook issues.
 */
const ManagedDialogInternal: React.FC<{
  modalId: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
  [key: string]: unknown;
}> = ({ modalId, open, onOpenChange, children, ...props }) => {
  const modalManager = useModalManager();
  const hasRegisteredRef = React.useRef(false);
  
  // Create a stable close callback
  const closeCallback = React.useCallback(() => {
    onOpenChange?.(false);
  }, [onOpenChange]);
  
  // Handle open state changes with modal management
  React.useEffect(() => {
    if (open) {
      // Register with modal manager when opening
      modalManager.openModal(modalId, closeCallback);
      hasRegisteredRef.current = true;
    } else if (hasRegisteredRef.current) {
      // Notify modal manager when closing
      modalManager.closeModal(modalId);
      hasRegisteredRef.current = false;
    }
    
    // Cleanup on unmount
    return () => {
      if (hasRegisteredRef.current) {
        modalManager.closeModal(modalId);
        hasRegisteredRef.current = false;
      }
    };
  }, [open, modalId, modalManager, closeCallback]);
  
  // Intercept onOpenChange to coordinate with modal manager
  const handleOpenChange = React.useCallback((newOpen: boolean) => {
    if (newOpen) {
      // Opening - register with modal manager first
      modalManager.openModal(modalId, closeCallback);
    } else {
      // Closing - notify modal manager
      modalManager.closeModal(modalId);
    }
    onOpenChange?.(newOpen);
  }, [modalId, modalManager, closeCallback, onOpenChange]);
  
  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={handleOpenChange}
      {...props}
    >
      {children}
    </DialogPrimitive.Root>
  );
};

/**
 * Dialog component with optional modal management integration.
 * When modalId is provided, the dialog will automatically coordinate
 * with other managed modals to ensure only one is open at a time.
 */
const Dialog: React.FC<DialogProps> = ({ modalId, open, onOpenChange, children, ...props }) => {
  // If no modalId provided, use standard Dialog behavior
  if (!modalId) {
    return (
      <DialogPrimitive.Root open={open} onOpenChange={onOpenChange} {...props}>
        {children}
      </DialogPrimitive.Root>
    );
  }
  
  // Use managed dialog with modal coordination
  return (
    <ManagedDialogInternal
      modalId={modalId}
      open={open}
      onOpenChange={onOpenChange}
      {...props}
    >
      {children}
    </ManagedDialogInternal>
  );
};
Dialog.displayName = "Dialog";

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-4 rounded-[10px] opacity-70 ring-offset-background transition-all hover:opacity-100 active:border-green-500 active:bg-green-50 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground border border-[#BCC9DE] h-10 w-10 flex items-center justify-center group">
        <X className="h-6 w-6 fill-[#293444] transition-all group-hover:[filter:invert(48%)_sepia(79%)_saturate(2476%)_hue-rotate(86deg)_brightness(118%)_contrast(119%)] group-active:[filter:invert(48%)_sepia(79%)_saturate(2476%)_hue-rotate(86deg)_brightness(118%)_contrast(119%)]" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
